const DEFAULT_CURRENCY_CODE = 'GHS';
const ISO_CURRENCY_CODE_PATTERN = /^[A-Z]{3}$/;
const MONEY_PATTERN = /^-?\d+(?:\.\d+)?$/;

export function resolveCurrencyCode(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') {
      continue;
    }

    const normalized = candidate.trim().toUpperCase();
    if (ISO_CURRENCY_CODE_PATTERN.test(normalized)) {
      return normalized;
    }
  }

  return DEFAULT_CURRENCY_CODE;
}

export function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function toPositiveFiniteNumber(value: unknown): number | null {
  const parsed = toFiniteNumber(value);
  return parsed !== null && parsed > 0 ? parsed : null;
}

export function toPriceString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  const parsed = toFiniteNumber(value);
  return parsed === null ? null : String(parsed);
}

export function getFirstFiniteNumber(...values: unknown[]): number | null {
  for (const value of values) {
    const parsed = toFiniteNumber(value);
    if (parsed !== null) {
      return parsed;
    }
  }

  return null;
}

export function getFirstPriceString(...values: unknown[]): string | null {
  for (const value of values) {
    const parsed = toPriceString(value);
    if (parsed !== null) {
      return parsed;
    }
  }

  return null;
}

function parseMoneyToMinorUnits(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  const raw = typeof value === 'string' ? value.trim() : String(value).trim();
  if (!raw || !MONEY_PATTERN.test(raw)) {
    return null;
  }

  const negative = raw.startsWith('-');
  const absolute = negative ? raw.slice(1) : raw;
  const [wholePart, fractionPart = ''] = absolute.split('.');
  const roundingDigits = `${fractionPart}000`.slice(0, 3);

  let minorUnits = Number(wholePart || '0') * 100 + Number(roundingDigits.slice(0, 2) || '0');
  if (Number(roundingDigits[2] || '0') >= 5) {
    minorUnits += 1;
  }

  return negative ? -minorUnits : minorUnits;
}

function formatMinorUnits(minorUnits: number): string {
  const negative = minorUnits < 0;
  const absolute = negative ? -minorUnits : minorUnits;
  const whole = Math.floor(absolute / 100);
  const fraction = String(absolute % 100).padStart(2, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

export function normalizeMoneyString(value: unknown): string | null {
  const minorUnits = parseMoneyToMinorUnits(value);
  return minorUnits === null ? null : formatMinorUnits(minorUnits);
}

export function subtractMoneyValues(minuend: unknown, subtrahend: unknown): string | null {
  const left = parseMoneyToMinorUnits(minuend);
  const right = parseMoneyToMinorUnits(subtrahend);

  if (left === null || right === null) {
    return null;
  }

  return formatMinorUnits(left - right);
}

export function compareMoneyValues(left: unknown, right: unknown): number | null {
  const leftMinorUnits = parseMoneyToMinorUnits(left);
  const rightMinorUnits = parseMoneyToMinorUnits(right);

  if (leftMinorUnits === null || rightMinorUnits === null) {
    return null;
  }

  if (leftMinorUnits === rightMinorUnits) {
    return 0;
  }

  return leftMinorUnits > rightMinorUnits ? 1 : -1;
}

type WeeklyPlanPricingOptions = {
  available?: boolean | null;
  as_low_as_daily?: number | null;
  as_low_as_weekly?: number | null;
  as_low_as_monthly?: number | null;
};

export function resolveWeeklyPlanAmount(
  options?: WeeklyPlanPricingOptions | null
): number | null {
  if (!options || options.available === false) {
    return null;
  }

  const weekly = toPositiveFiniteNumber(options.as_low_as_weekly);
  if (weekly !== null) {
    return weekly;
  }

  const daily = toPositiveFiniteNumber(options.as_low_as_daily);
  if (daily !== null) {
    return Math.ceil(daily * 7);
  }

  const monthly = toPositiveFiniteNumber(options.as_low_as_monthly);
  if (monthly !== null) {
    return Math.ceil(monthly / 4.33);
  }

  return null;
}

export function resolveDailyPlanAmount(
  options?: WeeklyPlanPricingOptions | null
): number | null {
  if (!options || options.available === false) {
    return null;
  }

  const daily = toPositiveFiniteNumber(options.as_low_as_daily);
  if (daily !== null) {
    return daily;
  }

  const weekly = toPositiveFiniteNumber(options.as_low_as_weekly);
  if (weekly !== null) {
    return Math.ceil(weekly / 7);
  }

  const monthly = toPositiveFiniteNumber(options.as_low_as_monthly);
  if (monthly !== null) {
    return Math.ceil(monthly / 30);
  }

  return null;
}
